import React from 'react';
import { Text, Heading } from '@/components/ui/design-system';

export const PrivacyPolicyContent = () => (
  <>
    <Heading level={2} className="heading-underline-animated pb-2 block">Introduction</Heading>
    <Text size="lg" className="mt-4 mb-5">
      At PAVISHA, we respect your privacy and are committed to protecting your personal data. 
      This privacy policy explains how we collect, use, disclose, and safeguard your information 
      when you visit our website or use our services.
    </Text>
    <Text size="lg" className="mt-4 mb-5">
      Please read this privacy policy carefully. If you do not agree with the terms of this privacy 
      policy, please do not access the site.
    </Text>
    
    <Heading level={2} className="heading-underline-animated pb-2 block">Information We Collect</Heading>
    <Text size="lg" className="mt-4 mb-5">
      We may collect information about you in a variety of ways. The information we may collect 
      via the website includes:
    </Text>
    
    <Heading level={3} className="heading-underline-animated pb-2 block mt-8">Personal Data</Heading>
    <Text size="lg" className="mt-4 mb-5">
      When using our services or contacting us, we may request personally identifiable information 
      such as your name, email address, postal address, and phone number. This information is 
      collected with your consent and is used to fulfill your requests and improve our services.
    </Text>
    
    <Heading level={3} className="heading-underline-animated pb-2 block mt-8">Derivative Data</Heading>
    <Text size="lg" className="mt-4 mb-5">
      Our servers automatically collect information about your browsing patterns and device information 
      when you visit the website. This data may include your IP address, browser type, operating system, 
      access times, and the pages you view. This information helps us analyze trends, administer the site, 
      and improve user experience.
    </Text>
    
    <Heading level={3} className="heading-underline-animated pb-2 block mt-8">Financial Data</Heading>
    <Text size="lg" className="mt-4 mb-5">
      We may collect financial information, such as payment method details, when you make a purchase. 
      This information is used solely to complete your transactions and prevent fraud.
    </Text>

    <Heading level={2} className="heading-underline-animated pb-2 block">Use of Your Information</Heading>
    <Text size="lg" className="mt-4 mb-5">
      We may use the information we collect from you in the following ways:
    </Text>
    <ul className="list-disc list-inside space-y-2 mt-4 mb-5">
      <li>To process and fulfill your orders</li>
      <li>To provide you with information, products, or services that you request from us</li>
      <li>To improve our website and user experience</li>
      <li>To personalize your experience and deliver content relevant to your interests</li>
      <li>To provide customer service and respond to your inquiries</li>
      <li>To send periodic emails regarding your orders or other products and services</li>
      <li>To protect our rights and interests, as well as the rights and interests of others</li>
    </ul>

    <Heading level={2} className="heading-underline-animated pb-2 block">Disclosure of Your Information</Heading>
    <Text size="lg" className="mt-4 mb-5">
      We may share information we have collected about you in certain situations. Your information 
      may be disclosed as follows:
    </Text>
    
    <Heading level={3} className="heading-underline-animated pb-2 block mt-8">By Law or to Protect Rights</Heading>
    <Text size="lg" className="mt-4 mb-5">
      If we believe the release of information is necessary to comply with the law, enforce our site 
      policies, or protect our or others' rights, property, or safety, we may share your information.
    </Text>
    
    <Heading level={3} className="heading-underline-animated pb-2 block mt-8">Business Partners</Heading>
    <Text size="lg" className="mt-4 mb-5">
      With your consent, we may share your information with our business partners to offer you certain 
      products or services.
    </Text>
    
    <Heading level={3} className="heading-underline-animated pb-2 block mt-8">Third-Party Service Providers</Heading>
    <Text size="lg" className="mt-4 mb-5">
      We may share your information with third parties that perform services for us, such as payment 
      processing, data analysis, email delivery, hosting services, and customer service.
    </Text>

    <Heading level={2} className="heading-underline-animated pb-2 block">Security of Your Information</Heading>
    <Text size="lg" className="mt-4 mb-5">
      We use administrative, technical, and physical security measures to protect your personal 
      information. While we have taken reasonable steps to secure the personal information you 
      provide to us, please be aware that no security measures are perfect or impenetrable, and 
      we cannot guarantee that your information will not be accessed, disclosed, altered, or 
      destroyed by breach of any of our physical, technical, or managerial safeguards.
    </Text>

    <Heading level={2} className="heading-underline-animated pb-2 block">Cookie Policy</Heading>
    <Text size="lg" className="mt-4 mb-5">
      We use cookies and similar tracking technologies to track activity on our website and store 
      certain information. You can instruct your browser to refuse all cookies or to indicate when 
      a cookie is being sent. However, if you do not accept cookies, you may not be able to use 
      some portions of our site. For more information, please see our <a href="/cookies">Cookie Policy</a>.
    </Text>

    <Heading level={2} className="heading-underline-animated pb-2 block">Children's Privacy</Heading>
    <Text size="lg" className="mt-4 mb-5">
      Our website is not intended for children under 13 years of age. We do not knowingly collect 
      personal information from children under 13. If you are under 13, please do not provide any 
      information on this website.
    </Text>

    <Heading level={2} className="heading-underline-animated pb-2 block">Your Rights</Heading>
    <Text size="lg" className="mt-4 mb-5">
      You have certain rights regarding your personal information:
    </Text>
    <ul className="list-disc list-inside space-y-2 mt-4 mb-5">
      <li>The right to access the personal information we have about you</li>
      <li>The right to request correction of inaccurate personal information</li>
      <li>The right to request deletion of your personal information</li>
      <li>The right to object to processing of your personal information</li>
      <li>The right to data portability</li>
      <li>The right to withdraw consent</li>
    </ul>
    <Text size="lg" className="mt-4 mb-5">
      To exercise these rights, please contact us using the information provided below.
    </Text>

    <Heading level={2} className="heading-underline-animated pb-2 block">Changes to This Privacy Policy</Heading>
    <Text size="lg" className="mt-4 mb-5">
      We may update our privacy policy from time to time. We will notify you of any changes by 
      posting the new privacy policy on this page and updating the "Last Updated" date at the top 
      of this page. You are advised to review this privacy policy periodically for any changes.
    </Text>
  </>
);
